import { fetchWrapper, sendErrorMessage, SERVER, sleep } from "./helpers";

export const MAX_STEP_RETRIES = 10;

const getFeedErrors = (feedItem) => {
  const errors = [];

  try {
    if (feedItem.errors) {
      feedItem.errors.forEach((e) => {
        try {
          const err = `${e.type} - ${e.description}`;
          errors.push(err);
        } catch (innerError) {
          console.error("Error processing feedItem.errors entry:", innerError);
        }
      });
    }
  } catch (error) {
    console.error("Error processing feedItem.errors:", error);
  }

  try {
    if (feedItem.ingestionErrors && feedItem.ingestionErrors.ingestionError) {
      feedItem.ingestionErrors.ingestionError.forEach((e) => {
        try {
          const err = `${e.type} - ${e.description}`;
          errors.push(err);
        } catch (innerError) {
          console.error("Error processing feedItem.ingestionErrors.ingestionError entry:", innerError);
        }
      });
    }
  } catch (error) {
    console.error("Error processing feedItem.ingestionErrors:", error);
  }

  try {
    if (feedItem.itemDetails && feedItem.itemDetails.itemIngestionStatus) {
      feedItem.itemDetails.itemIngestionStatus.forEach((status) => {
        try {
          if (!["INPROGRESS", "SUCCESS"].includes(status.ingestionStatus)) {
            if (status.ingestionErrors && status.ingestionErrors.ingestionError) {
              status.ingestionErrors.ingestionError.forEach((e) => {
                try {
                  const err = `${e.type} - ${e.description}`;
                  errors.push(err);
                } catch (innerError) {
                  console.error("Error processing status.ingestionErrors.ingestionError entry:", innerError);
                }
              });
            }
          }
        } catch (innerError) {
          console.error("Error processing feedItem.itemDetails.itemIngestionStatus entry:", innerError);
        }
      });
    }
  } catch (error) {
    console.error("Error processing feedItem.itemDetails:", error);
  }

  return errors;
};

export const checkFeed = async (item, user, password, channel, page, encodedAuth) => {
  const feedId = item.step > 2 ? item.wfsFeedId : item.feedId;
  // Convert 15 seconds to milliseconds
  const sec15 = 1 * 15 * 1000; // 15 seconds
  const maxRetries = 80; // Maximum number of retries for TIME_BRAKE status

  // If item is in TIME_BRAKE status and last request was less than 1 minute ago, skip checking
  if (item.status === "TIME_BRAKE") {
    const lastRequestTime = new Date(item.lastRequest).getTime();
    const elapsed = Date.now() - lastRequestTime;

    if (elapsed < sec15) {
      return item; // Not time to retry yet
    }

    // Check if we've exceeded the maximum retries
    if (item.retryCount && item.retryCount >= maxRetries) {
      sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "checkFeed", "WARNING", [`Maximum retry attempts exceeded in checkFeed for Feed ID: ${feedId}`]);
      return {
        ...item,
        status: "WARNING",
        unpublishedReasons: {
          reason: [...(item.unpublishedReasons?.reason || []), "It’s taking a bit longer than usual. Please wait a moment and try again. Walmart updates can sometimes take up to 4 hours."],
        },
        lastRequest: new Date().toISOString()
      };
    }
  }

  const url = `${SERVER}/feed_item_status`;
  const body = {
    feed_id: feedId
  };

  try {
    const feedData = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
    const errors = getFeedErrors(feedData);

    // Reset step retry count on successful API call
    const stepRetryCount = 0;

    if (feedData.feedStatus === "RECEIVED" || feedData.feedStatus === "INPROGRESS") {
      // Calculate retry count
      const retryCount = (item.retryCount || 0) + 1;

      // Set to TIME_BRAKE state and mark when we should retry
      const updatedItem = {
        ...item,
        status: "TIME_BRAKE",
        feedStatus: feedData.feedStatus,
        lastRequest: new Date().toISOString(),
        retryCount: retryCount,
        stepRetryCount
        // Don't increment step for RECEIVED status
      };

      return updatedItem;
    }

    if (errors.length || feedData.feedStatus === "ERROR") {
      sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "checkFeed", "WARNING", [`item: ${item.itemId} have error in feed ${feedId} on step ${item.step}`, ...errors], body);
      const updatedItem = {
        ...item,
        status: "WARNING",
        unpublishedReasons: { reason: errors || [`item: ${item.itemId} have error in feed ${feedId} on step ${item.step}`], },
        feedStatus: feedData.feedStatus,
        lastRequest: new Date().toISOString(),
        step: item.step + 1,
        stepRetryCount
      };
      return updatedItem;
    }

    const updatedItem = {
      ...item,
      status: "LOADING",
      feedStatus: feedData.feedStatus,
      lastRequest: new Date().toISOString(),
      step: item.step + 1,
      retryCount: 0, // Reset retry count on successful progress
      stepRetryCount
    };

    return updatedItem;
  } catch (error) {
    console.error(`Feed request failed for item ${item.sku.trim()}:`, error.message || error.toString());

    // Implement retry logic
    const stepRetryCount = (item.stepRetryCount || 0) + 1;

    // If we haven't reached max retries for this step, don't send error message yet
    if (stepRetryCount < MAX_STEP_RETRIES) {
      console.log(`Retrying step ${item.step} for item ${item.sku.trim()} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);
      return {
        ...item,
        stepRetryCount,
        lastRequest: new Date().toISOString()
      };
    }

    // If we've reached max retries, send error message and update status
    sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "checkFeed", "ERROR", [error.message || error.toString()], body);
    return {
      ...item,
      status: 'ERROR',
      feedStatus: 'ERROR',
      unpublishedReasons: { reason: [error.message || error.toString()] },
      lastRequest: new Date().toISOString(),
      stepRetryCount: 0 // Reset for next step
    };
  }
};

const getItemErrors = (tempItem) => {
  const errors = [];

  try {
    if (tempItem.unpublishedReasons && tempItem.unpublishedReasons.reason) {
      tempItem.unpublishedReasons.reason.forEach((e) => {
        try {
          errors.push(e);
        } catch (innerError) {
          console.error("Error processing tempItem.unpublishedReasons.reason entry:", innerError);
        }
      });
    }
  } catch (error) {
    console.error("Error processing tempItem.unpublishedReasons:", error);
  }

  return errors;
};

export const checkItem = async (item, user, password, channel, page, encodedAuth) => {
  // Convert 1 minute to milliseconds
  const sec15 = 1 * 15 * 1000;
  const maxRetries = 60; // Maximum number of retries for TIME_BRAKE status

  // If item is in TIME_BRAKE status and last request was less than 1 minute ago, skip checking
  if (item.status === "TIME_BRAKE") {
    const lastRequestTime = new Date(item.lastRequest).getTime();
    const elapsed = Date.now() - lastRequestTime;

    if (elapsed < sec15) {
      return item; // Not time to retry yet
    }

    // Check if we've exceeded the maximum retries
    if (item.retryCount && item.retryCount >= maxRetries) {
      sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "checkItem", "WARNING", [`Maximum retry attempts exceeded in checkItem for SKU: ${item.sku}`]);
      return {
        ...item,
        status: "WARNING",
        unpublishedReasons: {
          reason: [...(item.unpublishedReasons?.reason || []), "It’s taking a bit longer than usual. Please wait a moment and try again. Walmart updates can sometimes take up to 4 hours."],
        },
        lastRequest: new Date().toISOString()
      };
    }
  }
  const body = { sku: item.sku.trim() };
  const url = `${SERVER}/get_an_item`;
  try {
    const itemData = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body });
    const tempItem = itemData.ItemResponse[0];
    const errors = getItemErrors(tempItem);

    // Reset step retry count on successful API call
    const stepRetryCount = 0;

    if (errors.length || tempItem.publishedStatus === "ERROR") {
      sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "checkItem", "WARNING", [`item: ${item.itemId} have error (sku: ${item.sku.trim()}) on step ${item.step}`, ...errors], body);
      const updatedItem = {
        ...item,
        status: "WARNING",
        unpublishedReasons: { reason: errors || [`item: ${item.itemId} have error (sku: ${item.sku.trim()}) on step ${item.step}`], },
        publishedStatus: tempItem.publishedStatus,
        anItem: tempItem,
        lastRequest: new Date().toISOString(),
        step: item.step + 1,
        stepRetryCount
      };
      return updatedItem;
    }

    const updatedItem = {
      ...item,
      status: "LOADING",
      publishedStatus: tempItem.publishedStatus,
      anItem: tempItem,
      lastRequest: new Date().toISOString(),
      step: item.step + 1,
      retryCount: 0, // Reset retry count on successful progress
      stepRetryCount
    };

    if ((item.isWfs || channel === "wfs") && item.MPItem) {
      updatedItem.MPItem = {
        ...item.MPItem,
        Orderable: {
          ...item.MPItem.Orderable,
          productIdentifiers: {
            productId: tempItem.gtin,
            productIdType: "GTIN"
          }
        },
        TradeItem: {
          ...item.MPItem.TradeItem,
          orderableGTIN: tempItem.gtin,
          each: {
            ...item.MPItem.TradeItem.each,
            eachGTIN: tempItem.gtin
          }
        }
      };
    }

    return updatedItem;
  } catch (error) {
    console.error(`Item request failed for ${item.sku.trim()}:`, error.message || error.toString());

    // Implement retry logic
    const stepRetryCount = (item.stepRetryCount || 0) + 1;

    // If we haven't reached max retries for this step, don't send error message yet
    if (stepRetryCount < MAX_STEP_RETRIES) {
      console.log(`Retrying step ${item.step} for item ${item.sku.trim()} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);

      // Calculate retry count for TIME_BRAKE
      const retryCount = (item.retryCount || 0) + 1;

      return {
        ...item,
        status: "TIME_BRAKE",
        stepRetryCount,
        retryCount: retryCount,
        lastRequest: new Date().toISOString()
      };
    }

    // If we've reached max retries, send error message and update status
    sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "checkItem", "ERROR", [error.message || error.toString()], body);
    return {
      ...item,
      status: 'ERROR',
      publishedStatus: 'SYSTEM_PROBLEM',
      unpublishedReasons: { reason: [error.message || error.toString()] },
      lastRequest: new Date().toISOString(),
      stepRetryCount: 0, // Reset for next step
      retryCount: 0 // Reset retry count
    };
  }
};

export const convertItemWFS = async (item, user, password, channel, page, encodedAuth) => {
  const url = `${SERVER}/convert_item_to_wfs`;
  const body = {
    item: item.MPItem
  }
  try {
    const { feedId } = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });

    // Reset step retry count on successful API call
    const stepRetryCount = 0;

    const updatedItem = {
      ...item,
      wfsFeedId: feedId,
      lastRequest: new Date().toISOString(),
      step: item.step + 1,
      stepRetryCount
    };

    return updatedItem;
  } catch (error) {
    console.error(`Convert Item to WFS request failed for ${item.sku.trim()}:`, error.message || error.toString());

    // Implement retry logic
    const stepRetryCount = (item.stepRetryCount || 0) + 1;

    // If we haven't reached max retries for this step, don't send error message yet
    if (stepRetryCount < MAX_STEP_RETRIES) {
      console.log(`Retrying step ${item.step} for item ${item.sku.trim()} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);
      return {
        ...item,
        stepRetryCount,
        lastRequest: new Date().toISOString()
      };
    }

    // If we've reached max retries, send error message and update status
    sendErrorMessage(user, password, channel, item.itemId, item.upc, page, item.step, "convertItemWFS", "ERROR", [error.message || error.toString()], body);
    return {
      ...item,
      status: 'ERROR',
      unpublishedReasons: { reason: [error.message || error.toString()] },
      lastRequest: new Date().toISOString(),
      stepRetryCount: 0 // Reset for next step
    };
  }
};

// Step 1: Upload to inventory as bulk (MP_ITEM_MATCH)
const executeStep1BulkUpload = async (items, user, password, encodedAuth, setProcessSteps, setAddedItems) => {
  try {
    await updateStepStatus(0, "inProgress", setProcessSteps);

    // Initialize items with processing metadata
    let processedItems = items.map(item => ({
      ...item,
      status: "LOADING",
      step: 0,
      lastRequest: null,
      retryCount: 0,
      stepRetryCount: 0
    }));
    await updateAddedItems(processedItems, setAddedItems);

    const bulkUploadResult = await sendBulkItemFeedMatch(items, user, password, encodedAuth);

    if (!bulkUploadResult.success) {
      await updateStepStatus(0, "failed", setProcessSteps);
      return {
        success: false,
        error: "Bulk upload failed",
        step: 0,
        items: processedItems
      };
    }

    const sharedFeedId = bulkUploadResult.feedId;
    processedItems = processedItems.map(item => ({
      ...item,
      feedId: sharedFeedId,
      status: "LOADING",
      lastRequest: new Date().toISOString(),
      step: 1
    }));

    await updateAddedItems(processedItems, setAddedItems);
    await updateStepStatus(0, "completed", setProcessSteps);

    return {
      success: true,
      items: processedItems
    };
  } catch (error) {
    await updateStepStatus(0, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 0,
      items
    };
  }
};

// Step 2: Check the bulk feed status (feed_item_status)
const executeStep2CheckFeedStatus = async (items, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page) => {
  try {
    await updateStepStatus(1, "inProgress", setProcessSteps);

    let processedItems = [...items];
    let feedCheckComplete = false;
    let checkAttempts = 0;
    const MAX_CHECK_ATTEMPTS = 20;
    let feedStatus = null;

    while (!feedCheckComplete && checkAttempts < MAX_CHECK_ATTEMPTS) {
      checkAttempts++;

      // Check feed status (using just one representative item since they share the same feed)
      const feedStatusResult = await checkFeed(processedItems[0], user, password, channel, page, encodedAuth);

      // Store feed status for future reference
      feedStatus = feedStatusResult;

      // Update all items with the same feed status
      processedItems = processedItems.map(item => ({
        ...item,
        status: feedStatusResult.status,
        feedStatus: feedStatusResult.feedStatus,
        lastRequest: feedStatusResult.lastRequest,
        retryCount: feedStatusResult.retryCount,
        stepRetryCount: feedStatusResult.stepRetryCount,
        unpublishedReasons: feedStatusResult.unpublishedReasons
      }));

      await updateAddedItems(processedItems, setAddedItems);

      // If feed processing is complete (either success or error)
      if (feedStatusResult.status !== "TIME_BRAKE") {
        feedCheckComplete = true;

        // Update step status based on feed check result
        if (feedStatusResult.status === "ERROR") {
          await updateStepStatus(1, "failed", setProcessSteps);
          return {
            success: false,
            error: "Feed processing failed",
            step: 1,
            items: processedItems
          };
        } else if (feedStatusResult.status === "WARNING") {
          await updateStepStatus(1, "warning", setProcessSteps);
          return {
            success: true,
            warning: true,
            message: "Feed processing completed with warnings",
            step: 1,
            items: processedItems
          };
        } else {
          await updateStepStatus(1, "completed", setProcessSteps);
        }
      } else {
        // Wait before checking again
        await sleep(2000);
      }
    }

    if (!feedCheckComplete) {
      await updateStepStatus(1, "warning", setProcessSteps);
      return {
        success: true,
        warning: true,
        message: "Feed check did not complete in the expected time",
        step: 1,
        items: processedItems
      };
    }

    await updateStepStatus(1, "completed", setProcessSteps);
    return {
      success: true,
      items: processedItems
    };
  } catch (error) {
    await updateStepStatus(1, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 1,
      items
    };
  }
};

// Step 3: Check all items in bulk statuses (get_an_item)
const executeStep3CheckItemStatuses = async (items, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page) => {
  try {
    await updateStepStatus(2, "inProgress", setProcessSteps);

    // Check status of each item
    const checkItemPromises = items.map(item => checkItem(item, user, password, channel, page, encodedAuth));
    const processedItems = await Promise.all(checkItemPromises);

    await updateAddedItems(processedItems, setAddedItems);

    // Calculate success rate
    const successCount = processedItems.filter(item => item.status !== "ERROR" && item.status !== "WARNING" && ["PUBLISHED", "UNPUBLISHED"].includes(item.publishedStatus)).length;
    const successRate = successCount / items.length;

    if (successRate < 1) { // Less than 100% success
      await updateStepStatus(2, "warning", setProcessSteps);

      // Return warning status with items - modal will be shown by coordinator
      return {
        success: true,
        warning: true,
        successCount,
        totalCount: items.length,
        step: 2,
        items: processedItems
      };
    }

    await updateStepStatus(2, "completed", setProcessSteps);
    return {
      success: true,
      items: processedItems
    };
  } catch (error) {
    await updateStepStatus(2, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 2,
      items
    };
  }
};

// Step 4: Convert all items to WFS in bulks by category (OMNI_WFS)
const executeStep4ConvertToWFS = async (items, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page) => {
  try {
    await updateStepStatus(3, "inProgress", setProcessSteps);

    let processedItems = items; // .map(item => item.MPItem);

    const result = await convertItemsToWFS(processedItems, user, password, channel, page, encodedAuth);

    // Update the feedId for all items in this category batch
    if (result.feedId) {
      processedItems = processedItems.map(item => {
        return {
          ...item,
          wfsFeedId: result.feedId,
          batchId: result.batchId,
          status: "LOADING",
          lastRequest: new Date().toISOString(),
          step: 4
        };
      });
      await updateAddedItems(processedItems, setAddedItems);
    }

    await updateStepStatus(3, "completed", setProcessSteps);
    return {
      success: true,
      items: processedItems
    };
  } catch (error) {
    await updateStepStatus(3, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 3,
      items
    };
  }
};

// Step 5: Check converting bulks statuses
const executeStep5CheckConvertStatus = async (items, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page) => {
  try {
    await updateStepStatus(4, "inProgress", setProcessSteps);

    let processedItems = [...items];
    const MAX_CHECK_ATTEMPTS = 20;

    // Group items by feedId
    const itemsByFeedId = {};
    processedItems.forEach(item => {
      if (item.wfsFeedId) {
        if (!itemsByFeedId[item.wfsFeedId]) {
          itemsByFeedId[item.wfsFeedId] = [];
        }
        itemsByFeedId[item.wfsFeedId].push(item);
      }
    });

    const feedStatuses = {};
    let allFeedsComplete = false;
    let checkAttempts = 0;

    while (!allFeedsComplete && checkAttempts < MAX_CHECK_ATTEMPTS) {
      checkAttempts++;
      let pendingFeeds = 0;

      // Check each feed
      for (const [feedId, feedItems] of Object.entries(itemsByFeedId)) {
        // Skip already completed feeds
        if (feedStatuses[feedId] && feedStatuses[feedId] !== "TIME_BRAKE") {
          continue;
        }

        // Use the first item to check the feed status
        const representativeItem = feedItems[0];
        const feedStatusResult = await checkFeed(representativeItem, user, password, channel, page, encodedAuth);

        // Store the feed status
        feedStatuses[feedId] = feedStatusResult.status;

        // Update all items in this feed with the status
        processedItems = processedItems.map(item => {
          if (item.wfsFeedId === feedId) {
            return {
              ...item,
              wfsStatus: feedStatusResult.status,
              wfsFeedStatus: feedStatusResult.feedStatus,
              lastRequest: feedStatusResult.lastRequest,
              retryCount: feedStatusResult.retryCount,
              stepRetryCount: feedStatusResult.stepRetryCount,
              unpublishedReasons: feedStatusResult.unpublishedReasons
            };
          }
          return item;
        });

        // Count feeds that are still processing
        if (feedStatusResult.status === "TIME_BRAKE") {
          pendingFeeds++;
        }
      }

      // Update the UI with current status
      await updateAddedItems(processedItems, setAddedItems);

      // Check if all feeds are complete
      if (pendingFeeds === 0) {
        allFeedsComplete = true;
      } else {
        // Wait before checking again
        await sleep(2000);
      }
    }

    const anyFeedError = Object.values(feedStatuses).some(status => status === "ERROR");
    const anyFeedWarning = Object.values(feedStatuses).some(status => status === "WARNING");

    if (anyFeedError) {
      await updateStepStatus(4, "failed", setProcessSteps);
      return {
        success: false,
        error: "WFS feed processing failed",
        step: 4,
        items: processedItems
      };
    } else if (!allFeedsComplete || anyFeedWarning) {
      await updateStepStatus(4, "warning", setProcessSteps);

      // Return warning status with items - modal will be shown by coordinator
      return {
        success: true,
        warning: true,
        successCount: processedItems.filter(item =>
          item.status !== "ERROR" &&
          item.status !== "WARNING" &&
          item.status !== "TIME_BRAKE"
        ).length,
        totalCount: processedItems.length,
        step: 4,
        items: processedItems
      };
    }

    await updateStepStatus(4, "completed", setProcessSteps);
    return {
      success: true,
      items: processedItems
    };
  } catch (error) {
    await updateStepStatus(4, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 4,
      items
    };
  }
};

// Step 6: Check all converting items
const executeStep6CheckItems = async (items, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page) => {
  try {
    await updateStepStatus(5, "inProgress", setProcessSteps);

    // Check status of each item
    const checkItemPromises = items.map(item => checkItem(item, user, password, channel, page, encodedAuth));
    const processedItems = await Promise.all(checkItemPromises);

    await updateAddedItems(processedItems, setAddedItems);

    // Calculate success rate
    const successCount = processedItems.filter(item => item.status !== "ERROR" && item.status !== "WARNING" && ["PUBLISHED", "UNPUBLISHED"].includes(item.publishedStatus)).length;
    const successRate = successCount / processedItems.length;

    if (successRate < 1) { // Less than 100% success
      await updateStepStatus(5, "warning", setProcessSteps);

      // Return warning status with items - modal will be shown by coordinator
      return {
        success: true,
        warning: true,
        successCount,
        totalCount: processedItems.length,
        step: 5,
        items: processedItems
      };
    }

    await updateStepStatus(5, "completed", setProcessSteps);
    return {
      success: true,
      items: processedItems
    };
  } catch (error) {
    await updateStepStatus(5, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 5,
      items
    };
  }
};

// Step 7: Check final publication status
const executeStep7FinalCheck = async (items, setProcessSteps, setAddedItems) => {
  try {
    await updateStepStatus(6, "inProgress", setProcessSteps);

    const finalSuccessItems = items.filter(item =>
      item.publishedStatus === "PUBLISHED" && item.status === "LOADING"
    );

    let processedItems = items.map(item => {
      if (item.publishedStatus === "PUBLISHED" && item.status === "LOADING") {
        return {
          ...item,
          status: "SUCCESS",
          step: 7
        };
      }
      return item;
    });

    await updateAddedItems(processedItems, setAddedItems);
    const allSuccessful = finalSuccessItems.length === processedItems.length;

    if (allSuccessful) {
      await updateStepStatus(6, "completed", setProcessSteps);
      return {
        success: true,
        items: processedItems,
        processComplete: true
      };
    } else {
      await updateStepStatus(6, "warning", setProcessSteps);

      // Return warning status with items - modal will be shown by coordinator
      return {
        success: true,
        warning: true,
        successCount: finalSuccessItems.length,
        totalCount: processedItems.length,
        step: 6,
        items: processedItems
      };
    }
  } catch (error) {
    await updateStepStatus(6, "failed", setProcessSteps);
    return {
      success: false,
      error: error.message,
      step: 6,
      items
    };
  }
};

// Main coordinator function that runs the process
export const runBulkProcessSteps = async (addedItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, startStep = 1, channel = "wfs") => {
  try {
    let currentItems = addedItems;

    // Step 1: Upload to inventory as bulk
    if (startStep <= 1) {
      let step1Result = await executeStep1BulkUpload(currentItems, user, password, encodedAuth, setProcessSteps, setAddedItems);

      if (!step1Result.success) {
        const modalResult = await showModal({
          title: "Step 1 Failed: Bulk Upload",
          message: step1Result.error,
          currentItems: currentItems,
          errorItems: [],
          warningItems: [],
          step: 1,
          canRetry: true
        });

        if (modalResult.action === "retry") {
          return runBulkProcessSteps(addedItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 1, channel);
        } else {
          return { success: false, error: step1Result.error, step: 1 };
        }
      }

      currentItems = step1Result.items;
      await sleep(5000);
    }

    // Step 2: Check the bulk feed status
    if (startStep <= 2) {
      let step2Result = await executeStep2CheckFeedStatus(currentItems, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page);

      if (!step2Result.success || step2Result.warning) {
        const errorItems = step2Result.items.filter(item => item.status === "ERROR");
        const warningItems = step2Result.items.filter(item => item.status === "WARNING");

        const modalResult = await showModal({
          title: step2Result.success ? "Step 2 Warning: Feed Status Check" : "Step 2 Failed: Feed Status Check",
          message: step2Result.error || step2Result.message || "Some items have issues",
          currentItems: currentItems,
          errorItems,
          warningItems,
          step: 2,
          canRetry: true,
          canRemove: errorItems.length > 0 || warningItems.length > 0
        });

        if (modalResult.action === "retry") {
          // Retry only this step with current items
          return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 2, channel);
        } else if (modalResult.action === "remove") {
          // Continue with only good items
          currentItems = modalResult.items;
        } else {
          return { success: false, error: "Process stopped by user", step: 2 };
        }
      } else {
        currentItems = step2Result.items;
      }
      await sleep(5000);
    }

    // Step 3: Check all items in bulk statuses
    if (startStep <= 3) {
      let step3Result = await executeStep3CheckItemStatuses(currentItems, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page);

      if (!step3Result.success || step3Result.warning) {
        const errorItems = step3Result.items.filter(item => item.status === "ERROR");
        const warningItems = step3Result.items.filter(item => item.status === "WARNING");

        const modalResult = await showModal({
          title: step3Result.success ? "Step 3 Warning: Item Status Check" : "Step 3 Failed: Item Status Check",
          message: step3Result.error || `${step3Result.successCount} of ${step3Result.totalCount} items successful`,
          currentItems: currentItems,
          errorItems,
          warningItems,
          step: 3,
          canRetry: true,
          canRemove: errorItems.length > 0 || warningItems.length > 0
        });

        if (modalResult.action === "retry") {
          // Retry from this step
          return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 3, channel);
        } else if (modalResult.action === "remove") {
          // Continue with only good items
          currentItems = modalResult.items;
        } else {
          return { success: false, error: "Process stopped by user", step: 3 };
        }
      } else {
        currentItems = step3Result.items;
      }
      await sleep(5000);

      if (channel === "sf") {
        return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 7, channel);
      }
    }

    // Step 4: Convert all items to WFS in bulks by category
    if (startStep <= 4) {
      let step4Result = await executeStep4ConvertToWFS(currentItems, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page);

      if (!step4Result.success) {
        const modalResult = await showModal({
          title: "Step 4 Failed: WFS Conversion",
          message: step4Result.error,
          currentItems: currentItems,
          errorItems: [],
          warningItems: [],
          step: 4,
          canRetry: true
        });

        if (modalResult.action === "retry") {
          // Retry from this step
          return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 4, channel);
        } else {
          return { success: false, error: step4Result.error, step: 4 };
        }
      }

      currentItems = step4Result.items;
      await sleep(5000);
    }

    // Step 5: Check converting bulks feeds statuses
    if (startStep <= 5) {
      let step5Result = await executeStep5CheckConvertStatus(currentItems, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page);

      if (!step5Result.success || step5Result.warning) {
        const errorItems = step5Result.items.filter(item =>
          item.status === "ERROR" || item.wfsStatus === "ERROR");
        const warningItems = step5Result.items.filter(item =>
          item.status === "WARNING" || item.wfsStatus === "WARNING" || item.status === "TIME_BRAKE");

        const modalResult = await showModal({
          title: step5Result.success ? "Step 5 Warning: Conversion Status Check" : "Step 5 Failed: Conversion Status Check",
          message: step5Result.error || `${step5Result.successCount} of ${step5Result.totalCount} items successfully converted`,
          currentItems: currentItems,
          errorItems,
          warningItems,
          step: 5,
          canRetry: true,
          canRemove: errorItems.length > 0 || warningItems.length > 0
        });

        if (modalResult.action === "retry") {
          // Retry from this step
          return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 5, channel);
        } else if (modalResult.action === "remove") {
          // Continue with only good items
          currentItems = modalResult.items;
        } else {
          return { success: false, error: "Process stopped by user", step: 5 };
        }
      } else {
        currentItems = step5Result.items;
      }
      await sleep(5000);
    }

    // Step 6: Check all converting items
    if (startStep <= 6) {
      let step6Result = await executeStep6CheckItems(currentItems, user, password, encodedAuth, setProcessSteps, setAddedItems, channel, page);

      if (!step6Result.success || step6Result.warning) {
        const errorItems = step6Result.items.filter(item => item.status === "ERROR");
        const warningItems = step6Result.items.filter(item => item.status === "WARNING");

        const modalResult = await showModal({
          title: step6Result.success ? "Step 6 Warning: Item Check" : "Step 6 Failed: Item Check",
          message: step6Result.error || `${step6Result.successCount} of ${step6Result.totalCount} items successful`,
          currentItems: currentItems,
          errorItems,
          warningItems,
          step: 6,
          canRetry: true,
          canRemove: errorItems.length > 0 || warningItems.length > 0
        });

        if (modalResult.action === "retry") {
          // Retry from this step
          return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 6, channel);
        } else if (modalResult.action === "remove") {
          // Continue with only good items
          currentItems = modalResult.items;
        } else {
          return { success: false, error: "Process stopped by user", step: 6 };
        }
      } else {
        currentItems = step6Result.items;
      }
      await sleep(5000);
    }

    // Step 7: Check final published statuses
    if (startStep <= 7) {
      let step7Result = await executeStep7FinalCheck(currentItems, setProcessSteps, setAddedItems);

      if (!step7Result.success || step7Result.warning) {
        const errorItems = step7Result.items.filter(item => item.status !== "SUCCESS");

        const modalResult = await showModal({
          title: step7Result.success ? "Step 7 Warning: Final Publication Status" : "Step 7 Failed: Final Publication Status",
          message: step7Result.error || `${step7Result.successCount} of ${step7Result.totalCount} items fully published`,
          currentItems: currentItems,
          errorItems,
          warningItems: [],
          step: 7,
          canRetry: true,
          canRemove: errorItems.length > 0
        });

        if (modalResult.action === "retry") {
          // Retry from this step
          return runBulkProcessSteps(currentItems, setProcessSteps, setAddedItems, user, password, encodedAuth, showModal, page, 7, channel);
        } else if (modalResult.action === "remove") {
          // Continue with only good items
          return {
            success: true,
            items: modalResult.items,
            processComplete: true,
            itemsRemoved: step7Result.items.length - modalResult.items.length
          };
        } else {
          return { success: false, error: "Process stopped by user", step: 7 };
        }
      }

      currentItems = step7Result.items;
      await sleep(5000);
    }

    // All steps completed successfully
    return {
      success: true,
      items: currentItems,
      processComplete: true
    };

  } catch (error) {
    console.error("Process error:", error);
    return { success: false, error: error.message, step: -1 };
  }
};

// Helper functions to communicate with server in bulks
const sendBulkItemFeedMatch = async (addedItems, user, password, encodedAuth) => {
  const items = addedItems.map((item) => {
    const code = item.upc;
    let p_type = 'UPC';
    if (code.length === 13) {
      p_type = 'EAN'
    } else if (code.length === 14) {
      p_type = 'GTIN'
    }
    return {
      "Item": {
        "sku": item.sku.trim(),
        "productIdentifiers": {
          "productIdType": p_type,
          "productId": code
        },
        "ShippingWeight": item.weight,
        "price": item.price?.amount,
        "condition": "New"
      }
    }
  });

  const scheme_MP_ITEM_MATCH = {
    "MPItemFeedHeader": {
      "processMode": "REPLACE",
      "subset": "EXTERNAL",
      "locale": "en",
      "sellingChannel": "mpsetupbymatch",
      "version": "4.2"
    },
    "MPItem": items
  };
  const url = `${SERVER}/send_item_feed_match_bulk`;
  const body = {
    scheme: scheme_MP_ITEM_MATCH
  }
  try {
    const response = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
    return {
      status: "LOADING",
      lastRequest: new Date().toISOString(),
      feedId: response.feedId,
      success: true
    };
  } catch (error) {
    console.error('Error:', error.message);
    return {
      status: "ERROR",
      lastRequest: new Date().toISOString(),
      success: false
    }
  }
};
const convertItemsToWFS = async (items, user, password, channel, page, encodedAuth) => {
  const url = `${SERVER}/convert_item_to_wfs`;
  const body = {
    item: items.map(item => item.MPItem)
  }
  try {
    const { feedId } = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });

    // Reset step retry count on successful API call
    const stepRetryCount = 0;

    const updatedItems = items.map(item => {
      return {
        ...item,
        wfsFeedId: feedId,
        lastRequest: new Date().toISOString(),
        step: item.step + 1,
        stepRetryCount
      }
    });

    return updatedItems;
  } catch (error) {
    console.error(`Convert Item to WFS request failed for ${items[0].sku.trim()}:`, error.message || error.toString());

    // Implement retry logic
    const stepRetryCount = (items[0].stepRetryCount || 0) + 1;

    // If we haven't reached max retries for this step, don't send error message yet
    if (stepRetryCount < MAX_STEP_RETRIES) {
      console.log(`Retrying step ${items[0].step} for item ${items[0].sku.trim()} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);
      return items.map(item => {
        return {
          ...item,
          lastRequest: new Date().toISOString(),
          stepRetryCount
        }
      });
    }

    // If we've reached max retries, send error message and update status
    sendErrorMessage(user, password, channel, items[0].itemId, items[0].upc, page, items[0].step, "convertItemWFS", "ERROR", [error.message || error.toString()], body);
    return items.map(item => {
      return {
        ...item,
        status: 'ERROR',
        unpublishedReasons: { reason: [error.message || error.toString()] },
        lastRequest: new Date().toISOString(),
        stepRetryCount: 0
      }
    });
  }
};

// Helper functions to update states
const updateStepStatus = async (stepIndex, status, setProcessSteps) => {
  setProcessSteps(prevSteps =>
    prevSteps.map(step =>
      step.index === stepIndex ? { ...step, status } : step
    )
  );

  // Small delay to ensure UI updates are visible to the user
  await sleep(300);
};
const updateAddedItems = async (processedItems, setAddedItems) => {
  setAddedItems(processedItems);

  // Small delay to ensure UI updates are visible to the user
  await sleep(300);
};